import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, AlertTriangle, ChevronLeft, ChevronRight, LayoutGrid, Table as TableIcon } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { defaultPolicy } from '../lib/issuePolicy';
import { ISSUE_SELECT_COLUMNS, LATENESS_CATEGORY, countAllIssues, countableIssues } from '../lib/issueCounting';
import {
  resolveRubricDimensions,
  getDimensionRatings,
  ratingToScore,
  QUALITY_SELECT_COLUMNS,
} from '../lib/qualityDimensions';

const PER_PAGE = 20;

const timeRangeOptions = [
  { label: '7d', value: 7 },
  { label: '28d', value: 28 },
  { label: '90d', value: 90 },
  { label: '1y', value: 365 },
  { label: 'All', value: null },
];

// Averages whatever dimensions the org's rubric defines (the five built-ins by
// default). Ratings are read through the shared helper so both `dimensions`
// JSONB rows and older legacy-column rows resolve correctly.
const getQuality = (qa, rubricDims) => {
  if (!qa || qa.insufficient_evidence) return 'N/A';
  const ratings = getDimensionRatings(qa);
  const scores = (rubricDims || [])
    .map(d => ratingToScore(ratings[d.key]))
    .filter(s => s > 0);
  if (scores.length === 0) return 'N/A';
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  if (avg >= 2.5) return 'Strong';
  if (avg >= 1.5) return 'Adequate';
  return 'Needs Development';
};

const Lessons = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [issueFilter, setIssueFilter] = useState('all');
  const [timeRange, setTimeRange] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem('tutortracer_lessons_view') || 'cards');

  const setLessonsView = (v) => {
    setView(v);
    localStorage.setItem('tutortracer_lessons_view', v);
  };

  useEffect(() => {
    if (!organizationId) return;
    loadSessions();
  }, [organizationId, page, search, issueFilter, timeRange]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      // With/No Issues must constrain the QUERY, not just the current page —
      // filtering client-side left the pager saying "1 of 132" over an empty
      // list, because the count came from the server and the rows were dropped
      // after it. Resolve the set of lessons that carry a countable issue first.
      let issueSessionIds = null;
      if (issueFilter !== 'all') {
        const { data: orgPolicyRow } = await supabase
          .from('organizations').select('issue_filter_policy')
          .eq('id', organizationId).maybeSingle();
        const pol = orgPolicyRow?.issue_filter_policy || defaultPolicy();
        const seen = new Set();
        for (let offset = 0; ; offset += 1000) {
          const { data: rk } = await supabase
            .from('risks_v2')
            .select(ISSUE_SELECT_COLUMNS)
            .eq('organization_id', organizationId)
            .neq('category', LATENESS_CATEGORY)
            .range(offset, offset + 999);
          const batch = rk || [];
          countableIssues(batch, pol).forEach(r => r.session_id && seen.add(r.session_id));
          if (batch.length < 1000) break;
        }
        issueSessionIds = Array.from(seen);
      }

      let query = supabase
        .from('sessions')
        .select(`
          id, tutor_name, student_name, lesson_timestamp, subject_covered, duration_minutes, risk_level,
          quality_assessments(${QUALITY_SELECT_COLUMNS}, insufficient_evidence)
        `, { count: 'exact' })
        .eq('organization_id', organizationId)
        .eq('is_processed_v2', true)
        .order('lesson_timestamp', { ascending: false })
        .range(page * PER_PAGE, (page + 1) * PER_PAGE - 1);

      if (issueSessionIds) {
        if (issueSessionIds.length === 0) {
          // No lesson has an issue: "with" is empty, "no" is unconstrained.
          if (issueFilter === 'with_issues') {
            setSessions([]); setTotal(0); setLoading(false); return;
          }
        } else if (issueFilter === 'with_issues') {
          query = query.in('id', issueSessionIds);
        } else {
          query = query.not('id', 'in', `(${issueSessionIds.join(',')})`);
        }
      }

      if (timeRange) {
        const d = new Date();
        d.setDate(d.getDate() - timeRange);
        query = query.gte('lesson_timestamp', d.toISOString());
      }

      if (search) {
        query = query.or(`tutor_name.ilike.%${search}%,student_name.ilike.%${search}%,subject_covered.ilike.%${search}%`);
      }

      // Org rubric decides which dimensions feed the per-lesson quality label;
      // issue_filter_policy feeds the shared issue-counting rules.
      const orgPromise = supabase
        .from('organizations')
        .select('quality_rubric, issue_filter_policy')
        .eq('id', organizationId)
        .maybeSingle();

      const [{ data, error, count }, { data: orgRow }] = await Promise.all([query, orgPromise]);
      if (error) throw error;

      const rubricDims = resolveRubricDimensions(orgRow?.quality_rubric);
      const issuePolicy = orgRow?.issue_filter_policy || defaultPolicy();
      const rows = data || [];
      const sessionIds = rows.map(s => s.id);

      // Per-lesson issue counts follow the shared rules in lib/issueCounting.js
      // (no tutor_lateness, no guardrail-suppressed rows) so the badge matches
      // what LessonDetail lists for the same lesson — every status, as there.
      const risksBySession = {};
      if (sessionIds.length > 0) {
        // One page of lessons is PER_PAGE ids, well inside PostgREST's limits.
        const { data: risks } = await supabase
          .from('risks_v2')
          .select(ISSUE_SELECT_COLUMNS)
          .eq('organization_id', organizationId)
          .neq('category', LATENESS_CATEGORY)
          .in('session_id', sessionIds);
        (risks || []).forEach(r => {
          if (!r) return;
          if (!risksBySession[r.session_id]) risksBySession[r.session_id] = [];
          risksBySession[r.session_id].push(r);
        });
      }

      const mapped = rows.map(s => {
        const qa = Array.isArray(s.quality_assessments) ? s.quality_assessments[0] : s.quality_assessments;
        return {
          ...s,
          quality: getQuality(qa, rubricDims),
          issuesCount: countAllIssues(risksBySession[s.id], issuePolicy),
        };
      });

      // The query already applied the issue filter, so the page is the result.
      setSessions(mapped);
      setTotal(count || 0);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
    setLoading(false);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Lessons
        </h1>
        <div className="flex items-center gap-2">
          <div className={`flex items-center rounded-lg border p-1 gap-0.5 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
            <button
              onClick={() => setLessonsView('cards')}
              title="Card view"
              className={`p-1.5 rounded-md transition-colors ${
                view === 'cards'
                  ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setLessonsView('table')}
              title="Table view"
              className={`p-1.5 rounded-md transition-colors ${
                view === 'table'
                  ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
              }`}
            >
              <TableIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className={`flex items-center rounded-lg border p-1 gap-0.5 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
            {timeRangeOptions.map(opt => (
              <button
                key={opt.label}
                onClick={() => { setTimeRange(opt.value); setPage(0); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  timeRange === opt.value
                    ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                    : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`} />
          <Input
            placeholder="Search tutor, student, subject..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className={`pl-9 h-10 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
          />
        </div>
        <div className={`flex items-center rounded-lg border p-1 gap-0.5 ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
          {['all', 'with_issues', 'no_issues'].map(f => (
            <button
              key={f}
              onClick={() => { setIssueFilter(f); setPage(0); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                issueFilter === f
                  ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
              }`}
            >
              {f === 'all' ? 'All' : f === 'with_issues' ? 'With Issues' : 'No Issues'}
            </button>
          ))}
        </div>
      </div>

      {/* Sessions Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-pulse">
          {[1,2,3,4,5,6,7,8].map(i => {
            const sk = isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]';
            return (
              <div key={i} className={`${cardClass} p-4`}>
                <div className="flex items-center justify-between">
                  <div className={`h-4 w-28 rounded ${sk}`} />
                </div>
                <div className={`h-3 w-20 rounded mt-2 ${sk}`} />
                <div className={`h-3 w-36 rounded mt-3 ${sk} opacity-50`} />
                <div className="flex items-center justify-between mt-4">
                  <div className={`h-5 w-16 rounded-full ${sk}`} />
                  <div className={`h-3 w-16 rounded ${sk} opacity-50`} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {view === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sessions.map(s => (
                <div
                  key={s.id}
                  onClick={() => navigate(`/lessons/${s.id}`)}
                  className={`${cardClass} p-4 cursor-pointer transition-all hover:border-[#3f3f46] group`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                      {s.tutor_name}
                    </span>
                    {s.issuesCount > 0 && (
                      <span className="flex items-center gap-1 text-xs text-amber-400">
                        <AlertTriangle className="h-3 w-3" /> {s.issuesCount}
                      </span>
                    )}
                  </div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>
                    with {s.student_name}
                  </div>
                  <div className={`text-xs mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
                    {s.subject_covered || 'No subject'} · {s.duration_minutes ? `${s.duration_minutes}m` : '—'}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      s.quality === 'Strong' ? isDark ? 'bg-[#586f8d]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]'
                      : s.quality === 'Adequate' ? isDark ? 'bg-[#52525b]/30 text-[#a1a1aa]' : 'bg-[#e5e5e5] text-[#666]'
                      : s.quality === 'Needs Development' ? isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-50 text-amber-700'
                      : isDark ? 'bg-[#27272a] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
                    }`}>{s.quality}</span>
                    <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
                      {new Date(s.lesson_timestamp).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'border-[#27272a] hover:bg-transparent' : 'border-[#e5e5e5] hover:bg-transparent'}>
                    <TableHead className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>Tutor</TableHead>
                    <TableHead className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>Student</TableHead>
                    <TableHead className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>Subject</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Duration</TableHead>
                    <TableHead className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>Quality</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Issues</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map(s => (
                    <TableRow
                      key={s.id}
                      onClick={() => navigate(`/lessons/${s.id}`)}
                      className={`cursor-pointer ${isDark ? 'border-[#27272a] hover:bg-[#18181b]' : 'border-[#e5e5e5] hover:bg-[#f9f9f9]'}`}
                    >
                      <TableCell className={`font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.tutor_name || '—'}</TableCell>
                      <TableCell className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>{s.student_name || '—'}</TableCell>
                      <TableCell className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>{s.subject_covered || '—'}</TableCell>
                      <TableCell className={`text-right ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.duration_minutes ? `${s.duration_minutes}m` : '—'}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          s.quality === 'Strong' ? isDark ? 'bg-[#586f8d]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]'
                          : s.quality === 'Adequate' ? isDark ? 'bg-[#52525b]/30 text-[#a1a1aa]' : 'bg-[#e5e5e5] text-[#666]'
                          : s.quality === 'Needs Development' ? isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-50 text-amber-700'
                          : isDark ? 'bg-[#27272a] text-[#52525b]' : 'bg-[#f0f0f0] text-[#999]'
                        }`}>{s.quality}</span>
                      </TableCell>
                      <TableCell className={`text-right ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                        {s.issuesCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-amber-400">
                            <AlertTriangle className="h-3 w-3" /> {s.issuesCount}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className={`text-right ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        {new Date(s.lesson_timestamp).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {sessions.length === 0 && (
            <div className={`text-center py-12 ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
              <BookOpen className="h-8 w-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No lessons found</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Lessons;
